import type {
    ComposableOptionsBase,
    CustomPagination,
    PaginatedPayload,
    AttributeValue,
    BundleTemplateLine,
    BundleTemplateLineSkuData,
    BundleTemplateLineOptionsData,
    BundleTemplateProcessedModel,
    BundleTemplateProcessedModelItem,
    BundleTemplateProcessed,
    BundleTemplateProcessedModelsMap,
    BundleTemplateProcessedLinesMap,
    BundleTemplateProcessedSkusMap,
    BundleTemplateProcessedImagesMap,
    BundleTemplate,
    GenericObject,
    ProductOrderOptionAttrs,
    FullImage,
    FullImageStack,
    ProductAttrsMaps
} from '~/types';
import {
    useApiUtils,
    useCustomFetch,
    useDebug
} from '~/composables';
import { isEqual as lo_isEqual } from 'es-toolkit';
import { omit as lo_omit } from 'es-toolkit';
import { xor as lo_xor } from 'es-toolkit';
import { castArray as lo_castArray } from 'es-toolkit/compat';
import { compact as lo_compact } from 'es-toolkit';
import type { NitroFetchOptions } from 'nitropack';
import type { NuxtError } from '#app';
import { sortWithSortOrderAscFn } from '~/utils';
import { createSharedComposable } from '@vueuse/core';

type ProcessedModelItem = {
    sku: Nullable<BundleTemplateLineSkuData>,
    processed: BundleTemplateProcessedModel
};

export interface UseBundleTemplatesOptions extends ComposableOptionsBase {}
export function useBundleTemplatesStandalone(options: Partial<UseBundleTemplatesOptions> = {}) {
    const apiBasePath = `/v1`;
    const useApiUtilsObj = useApiUtils();
    const $_fetch = useCustomFetch({ siteContext: options.siteContext });
    const debugConsole = useDebug();

    async function getBundleTemplates(uid?: string): Promise<PaginatedPayload<BundleTemplate> | NuxtError | Error> {
        const url = `${apiBasePath}/bundle-templates/`;
        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query: {
                all: true,
                product: uid || null
            }
        };

        let paginatedPayload: PaginatedPayload<BundleTemplate>;

        try {
            const response = await $_fetch<CustomPagination<BundleTemplate>>(url, config);

            paginatedPayload = {
                pagination: lo_omit(response, ['results']),
                data: response.results
            };

            return paginatedPayload;
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    // Validation

    function isProcessedModelValid(
        model: BundleTemplateProcessedModelItem,
        item: BundleTemplateLine
    ): boolean {
        switch (item.lineType) {
            case 'sku':
                if (!model.skuUid) return false;
            case 'option':
                if (!model.optionUid) return false;
        }

        if (!model.isInStock && !model.shipDate) return false;

        return true;
    }

    function isProcessedModelsValid(
        model: BundleTemplateProcessedModel,
        line: BundleTemplateLine
    ): boolean {
        return model.every((m) => isProcessedModelValid(m, line));
    }

    function validateProcessedModel(
        model: BundleTemplateProcessedModel,
        lines: BundleTemplateProcessedLinesMap
    ): Set<string> {
        return model.reduce((acc: Set<string>, m) => {
            const id = m.lineUid!;
            const item = lines.get(id);

            if (item && !isProcessedModelValid(m, item)) {
                // Mark invalid
                acc.add(id);
            }
            return acc;
        }, new Set<string>());
    }

    // Utils

    function processBundleTemplate(bundle: BundleTemplate): BundleTemplateProcessed {
        debugConsole.time('processBundleTemplate');
        const models: BundleTemplateProcessedModelsMap = new Map();
        const lines: BundleTemplateProcessedLinesMap = new Map();
        const skusMaps: BundleTemplateProcessedSkusMap = new Map();
        const imagesMaps: BundleTemplateProcessedImagesMap = new Map();
        const attrsMap: ProductAttrsMaps = { attrs: {}, attrsVals: {} };

        bundle.lines.forEach((line) => {
            const {
                uid: lineUid
            } = line;

            const lineDefaultAttrsVals: string[] = [];
            const orderedAttrVals: string[][] = [];

            line.optionsData = line.optionsData
                // Special sort to always raise non-color types above color
                .sort((a, b) => {
                    if (a.inputType === 'color') return 1;
                    if (b.inputType === 'color') return -1;
                    return sortWithSortOrderAscFn<BundleTemplateLineOptionsData>(a, b);
                })
                .map((o, i) => {
                    o.values = o.values.sort(sortWithSortOrderAscFn<AttributeValue>);
                    lineDefaultAttrsVals.push(o.defaultValueUid || o.values[0]?.uid || '');

                    // Create attrsMap
                    const attrUid = o.attributeUid;
                    const attrValUids = o.values.map((v) => v.uid);
                    attrsMap.attrs[attrUid] = attrValUids;
                    attrValUids.forEach((v) => attrsMap.attrsVals[v] = attrUid);

                    orderedAttrVals[i] = attrValUids;
                    return o;
                });

            const model: BundleTemplateProcessedModel = [];
            const skusMapItem: GenericObject<string[]> = {};
            const imagesMapItem: GenericObject<FullImage[]> = {};

            line.skusData?.forEach((sdObj) => {
                const { skuUid, optionUids, images } = sdObj;
                if (!lo_xor(lineDefaultAttrsVals, optionUids).length) {
                    lineDefaultAttrsVals.forEach((optionUid) => {
                        const attrUid = attrsMap.attrsVals[optionUid] || '';
                        model.push({
                            lineUid,
                            skuUid,
                            optionUid,
                            attrUid,
                            upcharge: 0,
                            isInStock: true,
                            shipDate: null,
                            status: null,
                            lineType: line.lineType
                        });
                    });
                }

                // Ordered Sku Opt Uids
                skusMapItem[skuUid] = optionUids.length > 1 ? optionUids.reduce((acc: string[], _, idx, arr) => {
                    const item = arr.find((u) => orderedAttrVals[idx]?.includes(u)) ?? 'err!';
                    acc.push(item);
                    return acc;
                }, []) : optionUids.slice(0);

                imagesMapItem[skuUid] = images;
            });

            models.set(lineUid, model);
            lines.set(lineUid, line);
            skusMaps.set(lineUid, skusMapItem);
            imagesMaps.set(lineUid, imagesMapItem);
        });

        debugConsole.timeEnd('processBundleTemplate');

        return {
            models,
            lines,
            skusMaps,
            imagesMaps,
            attrsMap
        };
    }

    function checkOptions(
        attrsMaps: GenericObject<string[]>,
        orderedOptAttrsVals: Ref<ProductOrderOptionAttrs>,
        optionsInvalids: Ref<string[][]>,
        callback: (disableValue: boolean, optionKey: string, value: string) => void
    ): void {
        const {
            attrs: optionsKeys,
            attrsVals: optionsValues
        } = orderedOptAttrsVals.value;

        optionsKeys.forEach((optKey: string, i: number) => {
            const options = attrsMaps[optKey];

            options?.forEach((optVal: string, j: number) => {
                const tempOptionsValues = optionsValues.slice(0);
                tempOptionsValues[i] = optVal;

                const disableValue = optionsInvalids.value.some(
                    (invVals: string[]) => lo_isEqual(invVals, tempOptionsValues)
                );

                callback(disableValue, optKey, optVal);
            });
        });
    }

    /**
    *  @param optionsModel
    *    [
    *       {"att_CjX8oK1Rrekcy": "attval_DuXPm2f4xgDi3"},
    *       {"att_A0uOB47UMKTyY": "attval_9rNmr2kPTzVkr"}
    *   ]
    */
    function getOrderedAttrsFromModel(
        optionsModel: GenericObject<string>[]
    ): ProductOrderOptionAttrs {
        return optionsModel
            .reduce((acc: ProductOrderOptionAttrs, model: GenericObject<string>) => {
                const attrUid = Object.keys(model)[0];
                const val = Object.values(model)[0];

                if (attrUid && val) {
                    acc.attrs.push(attrUid);
                    acc.attrsVals.push(val);
                }

                return acc;
            }, {
                attrs: [],
                attrsVals: []
            }
        );
    }

    function getSkuByAttrVal(
        skus: BundleTemplateLineSkuData[],
        attrValUid: string | string[]
    ): Nullable<BundleTemplateLineSkuData> {
        attrValUid = lo_castArray(attrValUid);
        return skus.find((sku) => {
            const { optionUids } = sku;
            if (!lo_xor(optionUids, attrValUid).length) {
                return sku;
            }
        }) ?? null;
    }

    function getImageStack(
        curBundleTmpl: BundleTemplateProcessed,
        model: BundleTemplateProcessedModel
    ): FullImageStack {
        const { imagesMaps } = curBundleTmpl;
        return model.reduce((acc: FullImageStack, o) => {
            const { skuUid, lineUid } = o;

            if (skuUid && lineUid) {
                const skuImages = imagesMaps.get(lineUid)?.[skuUid];

                skuImages?.forEach((imgObj) => {
                    const { groupKey } = imgObj;

                    if (groupKey) {
                        const item = mapGetOrSet<string, Set<FullImage>>(acc, groupKey, new Set());
                        item.add(imgObj);
                    }
                });
            }

            return acc;
        }, new Map());
    }

    function getProcessedModelItem(
        line: BundleTemplateLine,
        models: GenericObject<Nullable<string>>[]
    ): ProcessedModelItem {
        const {
            skusData,
            uid: lineUid
        } = line;
        const processed: BundleTemplateProcessedModel = [];
        const values = models.flatMap<string>((o) => lo_compact(Object.values(o)));
        const sku = getSkuByAttrVal(skusData, values);
        const skuUid = sku?.skuUid || null;

        const item: BundleTemplateProcessedModelItem = {
            lineUid,
            skuUid,
            optionUid: null,
            attrUid: null,
            upcharge: sku?.upcharge ?? 0,
            isInStock: sku?.isInStock ?? true,
            shipDate: sku?.shipDate ?? null,
            status: sku?.status ?? null,
            lineType: line.lineType
        };

        models.forEach((model) => {
            const k = Object.keys(model)[0]!;
            const v = model[k];
            if (!v) return;
            item.optionUid = v;
            item.attrUid = k;
            processed.push(item);
        });

        return {
            sku,
            processed
        };
    }

    function modelHydrate(
        model: GenericObject<GenericObject<Nullable<string>>[]>,
        bundle: BundleTemplateProcessed
    ): BundleTemplateProcessedModel {
        const output: BundleTemplateProcessedModel = [];
        const linesUids = Object.keys(model);

        linesUids.forEach((lineUid) => {
            const line = bundle.lines.get(lineUid);
            const modelItemList = model[lineUid];

            if (line && modelItemList?.length) {
                const {
                    processed
                } = getProcessedModelItem(line, modelItemList);

                output.push(...processed);
            }
        });

        return output;
    }

    function modelDehydrate(
        model: Nilish<BundleTemplateProcessedModel>
    ): GenericObject<GenericObject<Nullable<string>>[]> {
        return model?.reduce(
            (
                acc: GenericObject<GenericObject<Nullable<string>>[]>,
                line: BundleTemplateProcessedModelItem
            ) => {
                const { lineUid, attrUid, optionUid } = line;
                if (lineUid && attrUid && optionUid) {
                    acc[lineUid] = [{ [attrUid]:  optionUid }];
                }

                return acc;
            }, {}
        ) || {};
    }

    return {
        checkOptions,
        getBundleTemplates,
        processBundleTemplate,
        isProcessedModelValid,
        isProcessedModelsValid,
        validateProcessedModel,
        getOrderedAttrsFromModel,
        getImageStack,
        getProcessedModelItem,
        modelHydrate,
        modelDehydrate
    };
}

export const useBundleTemplates = createSharedComposable(useBundleTemplatesStandalone);
